import React, { useState, useCallback } from 'react';
import { Search, Camera, Mic } from 'lucide-react';
import { Button, Stack, Alert } from '@material-ui/core';

// Importamos el componente independiente del escáner
import BarcodeScanner from './BarcodeScanner';
import { buscarProducto } from '../../services/buscadorPrecios';

import './styles.css';

import imagenBuscar from '../../assets/images/buscador-precios/Buscar.svg';
import imagenBuscando from '../../assets/images/buscador-precios/Buscando.svg';

const SearchBar = ({ onResults, setNoEncontrado }) => {
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceError, setVoiceError] = useState(null);
    const [openScanner, setOpenScanner] = useState(false);
    
    // Buscar por código de barras
    const handleCodeDetected = async (code) => {
        if (!code) return;
        
        setIsLoading(true);
        try {
            const data = await buscarProducto({ "codigo_barras": code });
            
            onResults(data);
            
            if (data.length === 0) {
                setNoEncontrado(true);
            } else {
                setNoEncontrado(false);
                // Cerrar el escáner automáticamente si se encontró el producto
                setOpenScanner(false);
            }
            
            setQuery("");
        } catch (error) {
            console.error("Error al buscar por código de barras:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Función para iniciar el reconocimiento de voz
    const startVoiceRecognition = useCallback(() => {
        if (!('webkitSpeechRecognition' in window)) {
            setVoiceError('La búsqueda por voz no está soportada en este navegador');
            return;
        }

        const recognition = new (window).webkitSpeechRecognition();
        recognition.lang = 'es-MX';
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => {
            setIsListening(true);
            setVoiceError(null);
        };

        recognition.onresult = async (event) => {
            const text = event.results[0][0].transcript;
            setQuery(text);
            setIsListening(false);
            // Iniciar búsqueda automáticamente
            if (text.trim()) {
                setIsLoading(true);
                try {
                    const data = await buscarProducto({ "query": text });
                    onResults(data);
        
                    if (data.length === 0) {
                        setNoEncontrado(true);
                    } else {
                        setNoEncontrado(false);
                    }
        
                    setQuery("");
                } catch (error) {
                    console.error("Error en búsqueda por voz:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        recognition.onerror = (event) => {
            setVoiceError('Error en el reconocimiento de voz. Intenta de nuevo.');
            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        try {
            recognition.start();
        } catch (error) {
            setVoiceError('Error al iniciar el reconocimiento de voz');
            setIsListening(false);
        }
    }, [onResults, setNoEncontrado]);

    const handleSearch = async () => {
        if (!query.trim() || isLoading) return;
        
        setIsLoading(true);
        try {
            const data = await buscarProducto({ "query": query });
            onResults(data);

            if (data.length === 0) {
                setNoEncontrado(true);
            } else {
                setNoEncontrado(false);
            }

            setQuery("");
        } catch (error) {
            console.error("Error en búsqueda:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Manejar keypresses para buscar al presionar Enter
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="p-4 bg-white border-b">
            {voiceError && 
                <div className="bg-red-100 text-red-600 p-4 mb-4">
                    <Stack sx={{ width: '100%' }} spacing={2}>
                        <Alert severity="error" onClose={() => setVoiceError(null)}>{voiceError}</Alert>
                    </Stack>
                </div>
            }
            
            <div className="flex items-center gap-4 mb-4">
                <button className="flex items-center text-orange-500 font-semibold">
                    <Search size={20} className="mr-2" />
                    Busca tus Productos aquí
                </button>
            </div>
            <div className="flex gap-2 p-1">
                <div className="flex relative flex-1">
                    <input
                        type="text"
                        placeholder="Buscar producto..."
                        className="w-full p-3 border rounded-lg pr-24"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                    />
                </div>
                <button
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="boton"
                >
                    <img src={isLoading ? imagenBuscando : imagenBuscar} alt="Buscar" />
                </button>
            </div>
            <div className="flex gap-2 p-1">
                <div className="flex relative flex-1 justify-center">
                    <Button
                        variant="text"
                        size="small"
                        className={`p-0 ${isListening ? 'text-blue-500' : 'text-gray-400'}`}
                        onClick={startVoiceRecognition}
                        disabled={isListening}
                    >
                        <Mic className="h-5 w-5" />
                    </Button>
                    <Button
                        variant="text"
                        size="small"
                        className={`p-0 ${openScanner ? 'text-blue-500' : 'text-gray-400'}`}
                        onClick={() => setOpenScanner(true)}
                    >
                        <Camera className="h-5 w-5" />
                    </Button>
                </div>
            </div>
            <div className="flex gap-2">
                <div className="flex relative flex-1 justify-center">
                    <p>Habla o escanea un código de barras para buscar.</p>
                </div>
            </div>
            
            {/* Renderizamos el componente del escáner cuando se necesita */}
            {openScanner && (
                <BarcodeScanner
                    onCodeDetected={handleCodeDetected}
                    onClose={() => setOpenScanner(false)}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
};

export default SearchBar;